import { useState } from 'react';
import type { PolicySpec } from '@reef/schema';
import { api } from '../api.ts';

/**
 * The policy editor.
 *
 * Both halves of it — the visual controls and the chat box — emit the same
 * narrow operation list, which is then applied server-side and committed. That
 * shared vocabulary is what stops "only allow 10 rows from users" from being
 * able to quietly drop an unrelated deny pattern: the model cannot hand back a
 * whole new document, only a set of named edits.
 */
export function PolicyView({
  policies,
  onChanged,
}: {
  policies: PolicySpec[];
  onChanged(): void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(policies[0]?.id ?? null);
  const [pending, setPending] = useState<Operation[]>([]);
  const [changes, setChanges] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);

  const policy = policies.find((entry) => entry.id === selectedId) ?? null;

  const queue = (operation: Operation) => setPending((current) => [...current, operation]);

  const apply = async () => {
    if (!policy || pending.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.editPolicy(policy.id, pending, 'edited in the policy builder');
      setChanges(result.changes);
      setPending([]);
      onChanged();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Interpret a plain-language instruction locally.
   *
   * This deliberately handles only the phrasings it can parse unambiguously and
   * says so otherwise, rather than guessing. The full natural-language path is
   * the Builder agent, which has the whole catalog in context; this is the
   * fast path for the edits people actually type into a policy screen.
   */
  const interpret = () => {
    const text = instruction.trim().toLowerCase();
    if (!text) return;

    const tableRows = /(?:only\s+)?allow\s+(\d+)\s+rows?\s+from\s+(?:the\s+)?([a-z_][a-z0-9_]*)/.exec(text);
    if (tableRows) {
      queue({
        op: 'set_table_max_rows',
        table: tableRows[2]!,
        maxRows: Number(tableRows[1]),
      });
      setInstruction('');
      setError(null);
      return;
    }

    const rows = /(?:max|limit|only)\s+(\d+)\s+rows?/.exec(text);
    if (rows) {
      queue({ op: 'set_max_rows', maxRows: Number(rows[1]) });
      setInstruction('');
      setError(null);
      return;
    }

    if (/read[- ]only|no writes|block writes/.test(text)) {
      queue({ op: 'set_read_only', readOnly: true });
      setInstruction('');
      setError(null);
      return;
    }

    const redact = /redact\s+([a-z_,\s]+)/.exec(text);
    if (redact) {
      const known = ['email', 'phone', 'ssn', 'credit_card', 'ip_address', 'name', 'address', 'secret'];
      const classes = redact[1]!
        .split(/[,\s]+/)
        .map((word) => word.trim())
        .filter((word) => known.includes(word));
      if (classes.length > 0) {
        queue({ op: 'redact_pii', classes });
        setInstruction('');
        setError(null);
        return;
      }
    }

    const deny = /(?:hide|deny|drop)\s+(?:the\s+)?column[s]?\s+([a-z_,\s]+)/.exec(text);
    if (deny) {
      queue({
        op: 'deny_columns',
        columns: deny[1]!.split(/[,\s]+/).map((word) => word.trim()).filter(Boolean),
      });
      setInstruction('');
      setError(null);
      return;
    }

    setError(
      "I couldn't turn that into a policy change. Try something like “only allow 10 rows from users”, “make this read-only”, “redact email, ssn”, or “hide the columns ssn, phone”.",
    );
  };

  return (
    <div className="body">
      <aside className="sidebar">
        <p className="section-title">Policies</p>
        {policies.map((entry) => (
          <button
            key={entry.id}
            className="list-item"
            aria-current={entry.id === selectedId}
            onClick={() => {
              setSelectedId(entry.id);
              setPending([]);
              setChanges([]);
            }}
          >
            {entry.id}
            <small>
              {entry.maxRows} rows{entry.readOnly ? ' · read-only' : ''}
            </small>
          </button>
        ))}
      </aside>

      <main className="main">
        {!policy && <div className="empty">No policies yet.</div>}

        {policy && (
          <>
            <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>{policy.id}</h1>
            <p className="hint" style={{ marginTop: 0 }}>
              {policy.description ?? 'No description.'} · version {policy.version} ·{' '}
              <code>policies/{policy.id}.json</code>
            </p>

            {error && (
              <div className="banner danger">
                <h3>Not applied</h3>
                <p style={{ margin: 0 }}>{error}</p>
              </div>
            )}

            {changes.length > 0 && (
              <div className="banner warn">
                <h3>Committed</h3>
                <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                  {changes.map((change) => (
                    <li key={change}>{change}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="card">
              <h2>Change it in words</h2>
              <div className="stack">
                <input
                  value={instruction}
                  placeholder="only allow 10 rows from users"
                  onChange={(event) => setInstruction(event.target.value)}
                  onKeyDown={(event) => event.key === 'Enter' && interpret()}
                />
                <div className="row">
                  <button onClick={interpret} disabled={!instruction.trim()}>
                    Queue this change
                  </button>
                  <span className="hint">
                    Turns into the same operations the controls below emit — nothing a chat box can
                    do here is beyond what the visual editor can.
                  </span>
                </div>
              </div>
            </div>

            <div className="card">
              <h2>Current rules</h2>
              <div className="stack">
                <div className="row wrap">
                  <span className="pill accent">{policy.maxRows} rows max</span>
                  <span className="pill">{Math.round(policy.maxBytes / 1024)} KB max</span>
                  {policy.readOnly && <span className="pill warn">read-only</span>}
                  {policy.redactPii.map((klass) => (
                    <span key={klass} className="pill">
                      redact {klass}
                    </span>
                  ))}
                </div>

                {policy.tableAllowlist && (
                  <div className="field">
                    <label>Tables allowed</label>
                    <div className="row wrap">
                      {policy.tableAllowlist.map((table) => (
                        <span key={table} className="pill ok">
                          {table}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {policy.tables.length > 0 && (
                  <div className="field">
                    <label>Per-table rules</label>
                    <table>
                      <thead>
                        <tr>
                          <th>Table</th>
                          <th>Max rows</th>
                          <th>Columns</th>
                        </tr>
                      </thead>
                      <tbody>
                        {policy.tables.map((rule) => (
                          <tr key={rule.table}>
                            <td>
                              <code>{rule.table}</code>
                            </td>
                            <td>{rule.maxRows ?? policy.maxRows}</td>
                            <td>
                              {rule.allowedColumns
                                ? `only ${rule.allowedColumns.join(', ')}`
                                : rule.deniedColumns
                                  ? `all but ${rule.deniedColumns.join(', ')}`
                                  : 'all'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {policy.denyPatterns.length > 0 && (
                  <div className="field">
                    <label>Deny patterns</label>
                    <table>
                      <thead>
                        <tr>
                          <th>Id</th>
                          <th>Pattern</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {policy.denyPatterns.map((pattern) => (
                          <tr key={pattern.id}>
                            <td>{pattern.id}</td>
                            <td>
                              <code>{pattern.pattern}</code>
                            </td>
                            <td>
                              <span className={`pill ${pattern.action === 'block' ? 'danger' : 'warn'}`}>
                                {pattern.action}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <h2>Quick edits</h2>
              <div className="row wrap">
                <button onClick={() => queue({ op: 'set_max_rows', maxRows: 10 })}>
                  Cap at 10 rows
                </button>
                <button onClick={() => queue({ op: 'set_read_only', readOnly: true })}>
                  Make read-only
                </button>
                <button onClick={() => queue({ op: 'redact_pii', classes: ['email', 'phone', 'ssn'] })}>
                  Redact contact PII
                </button>
                <button
                  onClick={() =>
                    queue({ op: 'deny_columns', columns: ['password_hash', 'access_token'] })
                  }
                >
                  Deny credential columns
                </button>
              </div>
            </div>

            {pending.length > 0 && (
              <div className="card">
                <h2>Queued ({pending.length})</h2>
                <pre>{JSON.stringify(pending, null, 2)}</pre>
                <div className="row" style={{ marginTop: '0.75rem' }}>
                  <button className="primary" onClick={() => void apply()} disabled={busy}>
                    Commit to the repo
                  </button>
                  <button className="ghost" onClick={() => setPending([])}>
                    Discard
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

type Operation =
  | { op: 'set_max_rows'; maxRows: number }
  | { op: 'set_read_only'; readOnly: boolean }
  | { op: 'set_table_max_rows'; table: string; maxRows: number }
  | { op: 'deny_columns'; columns: string[]; table?: string }
  | { op: 'redact_pii'; classes: string[] };
